export const parseActivityDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    const trimmed = dateStr.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        const [y, m, d] = trimmed.slice(0, 10).split('-').map(Number);
        const date = new Date(y, m - 1, d);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    const slashParts = trimmed.split('/');
    if (slashParts.length === 3) {
        const day = Number(slashParts[0]);
        const month = Number(slashParts[1]);
        const year = Number(slashParts[2]);
        const date = new Date(year, month - 1, day);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const normalizeForecastHour = (timeStr) => {
    if (!timeStr) return '08:00:00';
    const parts = String(timeStr).trim().split(':');
    const hour = Number(parts[0]);
    if (Number.isNaN(hour)) return '08:00:00';
    const minutes = parts[1] ?? '00';
    const seconds = parts[2] ?? '00';
    return `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const parseHourFromTime = (timeStr) => {
    if (!timeStr) return null;
    const hour = Number(String(timeStr).trim().split(':')[0]);
    return Number.isNaN(hour) ? null : hour;
};

export const generateWeatherDataFromActivitySchedule = (
    activityStartDate,
    activityEndDate,
    activityStartTime,
    activityEndTime
) => {
    const start = parseActivityDate(activityStartDate);
    if (!start) {
        const now = new Date();
        return [{
            forecast_hour: '08:00:00',
            forecast_day: now.getDate(),
            forecast_month: now.getMonth() + 1,
            forecast_year: now.getFullYear(),
            value: 0,
        }];
    }

    const end = parseActivityDate(activityEndDate) || start;
    const startHour = parseHourFromTime(activityStartTime);
    const endHour = parseHourFromTime(activityEndTime);

    const useActivityTimes = startHour != null && endHour != null;
    const hourStart = useActivityTimes ? startHour : 8;
    const hourEndInclusive = useActivityTimes ? endHour - 1 : 20;

    const rows = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    while (cursor <= last) {
        const lastHour = useActivityTimes ? Math.max(hourStart, hourEndInclusive) : hourEndInclusive;
        for (let hour = hourStart; hour <= lastHour; hour += 1) {
            rows.push({
                forecast_hour: `${String(hour).padStart(2, '0')}:00:00`,
                forecast_day: cursor.getDate(),
                forecast_month: cursor.getMonth() + 1,
                forecast_year: cursor.getFullYear(),
                value: 0,
            });
        }
        cursor.setDate(cursor.getDate() + 1);
    }

    return rows.length > 0 ? rows : [{
        forecast_hour: normalizeForecastHour(activityStartTime),
        forecast_day: start.getDate(),
        forecast_month: start.getMonth() + 1,
        forecast_year: start.getFullYear(),
        value: 0,
    }];
};

export const weatherSlotKey = (row) => (
    `${row.forecast_year}-${row.forecast_month}-${row.forecast_day}-${normalizeForecastHour(row.forecast_hour)}`
);

export const mergeWeatherRowValues = (newRows, previousRows) => {
    if (!Array.isArray(previousRows) || previousRows.length === 0) {
        return newRows;
    }
    const valueBySlot = new Map(
        previousRows.map((row) => [weatherSlotKey(row), row.value])
    );
    return newRows.map((row) => {
        const key = weatherSlotKey(row);
        if (!valueBySlot.has(key)) {
            return row;
        }
        const previousValue = valueBySlot.get(key);
        return {
            ...row,
            value: previousValue === '' || previousValue === null || previousValue === undefined
                ? row.value
                : previousValue,
        };
    });
};

export const parseWeatherValue = (value) => {
    if (value === '' || value === null || value === undefined) {
        return 0;
    }
    const parsed = Number.parseFloat(String(value).replace(',', '.'));
    return Number.isNaN(parsed) ? 0 : parsed;
};

export const formatWeatherSlotLabel = (slot) => {
    if (!slot) return '';
    const day = String(slot.forecast_day ?? '').padStart(2, '0');
    const month = String(slot.forecast_month ?? '').padStart(2, '0');
    return `${day}/${month}/${slot.forecast_year} ${slot.forecast_hour}`;
};

export const extractForecastScheduleErrorDetail = (error) => {
    const detail = error?.response?.data?.detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        if (
            detail.missing_weather_data_slots
            || detail.unexpected_weather_data_slots
            || detail.expected_forecast_hours
        ) {
            return detail;
        }
    }
    return null;
};

export const buildWeatherDataFromScheduleError = (detail) => {
    const dates = detail?.expected_activity_dates || [];
    const hours = (detail?.expected_forecast_hours || []).map(normalizeForecastHour);

    if (dates.length === 0 || hours.length === 0) {
        return generateWeatherDataFromActivitySchedule(
            detail?.activity_start_date,
            detail?.activity_end_date || detail?.activity_start_date,
            detail?.activity_start_time,
            detail?.activity_end_time
        );
    }

    const rows = [];
    dates.forEach((dateStr) => {
        const date = parseActivityDate(dateStr);
        if (!date) return;
        hours.forEach((forecastHour) => {
            rows.push({
                forecast_hour: forecastHour,
                forecast_day: date.getDate(),
                forecast_month: date.getMonth() + 1,
                forecast_year: date.getFullYear(),
                value: 0,
            });
        });
    });

    return rows.length > 0 ? rows : generateWeatherDataFromActivitySchedule(
        detail?.activity_start_date,
        detail?.activity_end_date,
        detail?.activity_start_time,
        detail?.activity_end_time
    );
};
